/**
 * Canonical widget data shapes emitted by the AI agent.
 *
 * SOURCE OF TRUTH for widget payloads. Both the chat UI
 * (`web/src/components/AiChat/AiChatWidgetCard.tsx`) and the PDF service
 * (`AiAnalysisPdfService`) consume widgets typed against these interfaces.
 *
 * If the agent's `credit-analysis` skill changes a widget shape:
 *   1. Update this file in the api repo.
 *   2. Copy the same content into `web/src/components/AiChat/widget-types.ts`.
 *   3. The TypeScript compiler will flag both consumers if a renderer is
 *      out of step with the new shape.
 *
 * The two files MUST stay byte-identical. A drift test (string diff) can be
 * added to CI later; for now keep them in sync manually.
 */

// ─── Shared sub-types ─────────────────────────────────────────────────────

export interface ScoreFactor {
  code?: string;
  importance?: number;
  description: string;
}

export interface HighUtilizationAccount {
  type?: string;
  status?: string;
  utilization?: number;
  balance?: number;
  limit?: number;
}

export interface InquiryBreakdownEntry {
  type: string;
  count: number;
}

// ─── Per-widget data shapes ───────────────────────────────────────────────

export interface ScoreCardData {
  score?: number;
  color?: string;
  category?: string;
  modelName?: string;
  percentile?: number;
  topFactors?: ScoreFactor[];
}

export type RiskVerdict = 'APPROVE' | 'CONDITIONAL' | 'REFER' | 'DECLINE';
export type RiskLevel = 'low' | 'medium' | 'high' | 'very_high';

export interface RiskSummaryData {
  verdict?: RiskVerdict | string;
  tier?: number | null;
  riskLevel?: RiskLevel | string;
  recommendation?: string;
  positiveFactors?: string[];
  negativeFactors?: string[];
  conditions?: string[];
}

export interface DebtBreakdownData {
  installmentBalance?: number;
  revolvingBalance?: number;
  realEstateBalance?: number;
  totalMonthlyPayment?: number;
  revolvingUtilization?: number;
}

export interface PaymentHistoryChartData {
  onTimePercent?: number;
  monthsAnalyzed?: number;
  late30Count?: number;
  late60Count?: number;
  late90PlusCount?: number;
  derogatoryCount?: number;
}

export interface TradelineSummaryData {
  totalAccounts?: number;
  openAccounts?: number;
  derogatory?: number;
  oldestAccountDate?: string;
  recentInquiries6Mo?: number;
  accountsByType?: Record<string, number>;
}

export interface ProfileSummaryData {
  totalAccounts?: number;
  openAccounts?: number;
  satisfactory?: number;
  derogatory?: number;
  nowDelinquent?: number;
  wasDelinquent?: number;
  oldestTrade?: string;
  publicRecords?: number;
  disputedAccounts?: number;
}

export interface HighUtilizationData {
  accounts?: HighUtilizationAccount[];
}

export interface InquiryAnalysisData {
  inquiries6mo?: number;
  totalInquiries?: number;
  breakdown?: InquiryBreakdownEntry[];
  stackingSignal?: boolean;
}

export interface RelationshipSignalsData {
  samePersonOtherApplications?: Array<number | string>;
  otherOwnersAtAddress?: string[];
  notes?: string;
}

// ─── Discriminated union ─────────────────────────────────────────────────

export type AnalysisWidget =
  | { type: 'score_card'; data: ScoreCardData }
  | { type: 'risk_summary'; data: RiskSummaryData }
  | { type: 'debt_breakdown'; data: DebtBreakdownData }
  | { type: 'payment_history_chart'; data: PaymentHistoryChartData }
  | { type: 'tradeline_summary'; data: TradelineSummaryData }
  | { type: 'profile_summary'; data: ProfileSummaryData }
  | { type: 'high_utilization'; data: HighUtilizationData }
  | { type: 'inquiry_analysis'; data: InquiryAnalysisData }
  | { type: 'relationship_signals'; data: RelationshipSignalsData };

export type AnalysisWidgetType = AnalysisWidget['type'];

/**
 * Type-narrowing helper. Returns true and refines the widget when its `type`
 * matches one of the canonical kinds; returns false for unknown types so the
 * caller can render a graceful fallback.
 */
export function isKnownAnalysisWidget(
  w: { type?: string; data?: unknown } | null | undefined,
): w is AnalysisWidget {
  if (!w || typeof w.type !== 'string') return false;
  return (
    w.type === 'score_card' ||
    w.type === 'risk_summary' ||
    w.type === 'debt_breakdown' ||
    w.type === 'payment_history_chart' ||
    w.type === 'tradeline_summary' ||
    w.type === 'profile_summary' ||
    w.type === 'high_utilization' ||
    w.type === 'inquiry_analysis' ||
    w.type === 'relationship_signals'
  );
}
